import { FC, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Link from "@mui/material/Link";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
	useActivityCalls,
	useDismissCall,
	useUndismissCall,
} from "../../hooks/useActivityCalls";
import { CallItem } from "../../domain/activitySidebar.interface";
import { useParticipantLabel } from "./useParticipantLabel";
import CustomTooltip from "../styled/CustomTooltip";

interface TodaysActivitiesListProps {
	coordinatorId: string | undefined;
}

const formatRequestedAt = (iso: string | null): string =>
	iso ? dayjs(iso).format("hh:mm A") : "";

// SIMS-8934: a request reason can be up to 1000 chars, but a call has to stay
// one compact row in the sidebar, so long text is cut here and the whole
// message stays reachable on hover/focus rather than being dropped.
const REASON_MAX_CHARS = 50;

const CallReason: FC<{ reason: string }> = ({ reason }) => {
	// Whitespace-only counts as no reason given — same rule as SIMS-8473.
	const trimmed = reason.trim();
	if (!trimmed) return null;

	const isTruncated = trimmed.length > REASON_MAX_CHARS;
	const body = (
		<Box
			component="span"
			sx={{ fontStyle: "italic" }}
			// Only truncated text needs to be reachable; making every reason a tab
			// stop would add noise to the list for no benefit.
			tabIndex={isTruncated ? 0 : undefined}
		>
			{isTruncated ? `${trimmed.slice(0, REASON_MAX_CHARS)}…` : trimmed}
		</Box>
	);

	return isTruncated ? (
		<CustomTooltip title={trimmed} enterTouchDelay={0}>
			{body}
		</CustomTooltip>
	) : (
		body
	);
};

// SIMS-4162: the Today's Activities call list — active + (optionally) dismissed
// calls for the coordinator, with dismiss / un-dismiss and click-to-profile.
export const TodaysActivitiesList: FC<TodaysActivitiesListProps> = ({
	coordinatorId,
}) => {
	const navigate = useNavigate();
	const participantLabel = useParticipantLabel();
	const [showDismissed, setShowDismissed] = useState(false);
	const { data, isLoading } = useActivityCalls(coordinatorId, showDismissed);
	const dismiss = useDismissCall(coordinatorId);
	const undismiss = useUndismissCall(coordinatorId);

	const activeCalls = data?.activeCalls ?? [];
	const dismissedCalls = data?.dismissedCalls ?? [];
	const dismissedCount = data?.dismissedCount ?? 0;

	const renderRow = (call: CallItem, dismissed: boolean) => (
		<ListItem
			key={call.callId}
			disablePadding
			secondaryAction={
				<IconButton
					edge="end"
					size="small"
					color="primary"
					aria-label={dismissed ? "Un-dismiss call" : "Dismiss call"}
					onClick={(e) => {
						e.stopPropagation();
						if (dismissed) {
							undismiss.mutate(call.callId);
						} else {
							dismiss.mutate(call.callId);
						}
					}}
				>
					{dismissed ? (
						<UndoIcon fontSize="small" />
					) : (
						<CloseIcon fontSize="small" />
					)}
				</IconButton>
			}
			sx={{
				pl: 2,
				pr: 6,
				py: 1,
				...(dismissed ? { opacity: 0.5 } : {}),
			}}
		>
			{/* SIMS-7647: single click target — only this label navigates (AC3).
			    Text is participantLabel(call) only, never the raw name/CIN
			    field directly (AC4 leak-safe). */}
			<ListItemText
				sx={{ my: 0 }}
				primary={
					<Link
						component="button"
						variant="body2"
						underline="always"
						aria-label={`View participant ${participantLabel(call)}`}
						onClick={() => navigate(`/participants/${call.beneficiaryId}`)}
						sx={{
							color: (theme) => theme.palette.tertiary.main,
							textAlign: "left",
						}}
					>
						{participantLabel(call)}
					</Link>
				}
				secondary={
					<Box
						component="span"
						sx={{ display: "flex", flexDirection: "column" }}
					>
						{call.programName ? (
							<Box component="span" sx={{ color: "black" }}>
								{call.programName}
							</Box>
						) : null}
						<Box component="span">{formatRequestedAt(call.requestedAt)}</Box>
						{/* SIMS-8934: the reason the participant gave, when they gave one. */}
						{call.requestReason ? (
							<CallReason reason={call.requestReason} />
						) : null}
						{dismissed ? (
							<Box component="span" sx={{ color: "black" }}>
								Dismissed
							</Box>
						) : null}
					</Box>
				}
			/>
		</ListItem>
	);

	if (isLoading) {
		return (
			<Typography variant="body2" color="text.secondary">
				Loading…
			</Typography>
		);
	}

	const hasAny = activeCalls.length > 0 || dismissedCount > 0;

	return (
		<Box>
			{dismissedCount > 0 && (
				<Button
					size="small"
					variant="outlined"
					onClick={() => setShowDismissed((prev) => !prev)}
					sx={{ mb: 0.5, textTransform: "none" }}
				>
					{showDismissed
						? "Hide Dismissed"
						: `Show Dismissed (${dismissedCount})`}
				</Button>
			)}

			{activeCalls.length === 0 && (
				<Typography variant="body2" color="text.secondary">
					{hasAny ? "No active call requests" : "No call requests"}
				</Typography>
			)}

			<List dense disablePadding>
				{activeCalls.map((call) => renderRow(call, false))}
				{showDismissed && dismissedCalls.map((call) => renderRow(call, true))}
			</List>
		</Box>
	);
};

export default TodaysActivitiesList;
